import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  AccordionDetails,
  Button,
  Typography,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowUp as UpArrow,
  KeyboardArrowDown as DownArrow,
  InfoOutlined,
} from '@mui/icons-material';
import { accordionStyles } from '@/components/UserManagement/styles';
import {
  StyledAccordionSummary,
  StyledTableRow,
} from '../../Insights/InsightsExplorer/PointOfInterests/Workpacks/styles';
import { DataTable } from '@/components/shared/Table';
import {
  EsSheetStatus,
  GetRiskAssessmentSummaryDataQuery,
  StructureForRiskAssessmentQuery,
  useGetRiskAssessmentSummaryDataQuery,
  useGetRiskAssessmentSummaryTableDataQuery,
} from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { matchSorter } from 'match-sorter';
import { FiltersPanel } from '@/components/shared/FiltersPanel';
import { CalculationsMenu } from './CalculationsMenu';
import { useSetAtom } from 'jotai';
import { selectedRiskAssessmentFilters } from '../../state';
import { containedButtonStyle } from '@/theme';
import { useRouter, useFeatureFlag } from '@/hooks';
import { riskView } from '../state/atoms';
import { useFiltersForRisk } from '../hooks/useFiltersForRisk';
import { PageLoader } from '@/components/shared/PageLoader';
import { DisabledOverlay, StyledTableCell } from './styles';
import { RowData } from '@/components/shared/Table/DataTable';
import { GridCellParams } from '@mui/x-data-grid';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { FilterType } from '@/types';
import { ActionButton, getCriticalityLevelColor } from '../shared/styles';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';

type EsSheetSummaryTableProps = {
  structure: StructureForRiskAssessmentQuery['structure'] | undefined;
  handleUnpublish: () => void;
  loadingUnpublish: boolean;
  riskAssessmentDone: boolean;
};

const damageClassKeys = ['DC5', 'DC4', 'DC3', 'DC2', 'DC1', 'DC0', 'N/A'];
const riskCategoryKeys = ['RC5', 'RC4', 'RC3', 'RC2', 'RC1', 'RC0', 'N/A'];

const CellColorBox = ({ color }: { color: string }) => (
  <Box
    sx={{
      backgroundColor: color,
      borderRadius: '2px',
      width: '0.75rem',
      height: '0.75rem',
      display: 'inline-block',
      mr: 1,
    }}
  />
);
const statusColors: Record<string, string> = {
  Calculated: abyssColors.success[500],
  'In progress': abyssColors.tertiary[500],
  'Not calculated': abyssColors.primary[100],
};

const renderCalculationCell = (parameters: GridCellParams<RowData>, fieldName: string) => {
  const calculations = JSON.parse(parameters.row.calculations as string);
  const { total, [fieldName]: value } = calculations;

  let status = 'Not calculated';
  if (total === value) {
    status = 'Calculated';
  } else if (total > value && value > 0) {
    status = 'In progress';
  }

  const cellColor = statusColors[status];

  return (
    <Tooltip
      title={`${value}/${total} calculations performed`}
      placement="top"
      arrow
      slotProps={{
        popper: {
          sx: {
            '& .MuiTooltip-tooltip': {
              backgroundColor: '#000000B8',
              color: abyssColors.primary[50],
              opacity: 1,
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 500,
            },
            '& .MuiTooltip-arrow': {
              color: '#000000B8',
            },
          },
        },
      }}
    >
      <Stack height="100%" direction="row" alignItems="center" spacing={1}>
        <CellColorBox color={cellColor} />
        <Box sx={{ color: abyssColors.primary[300] }}>{status}</Box>
      </Stack>
    </Tooltip>
  );
};

const esSheetSubTableColumns = [
  { field: 'risk_level_2_component', headerName: 'Level 2 Component', flex: 1 },
  { field: 'risk_structure', headerName: 'Structure', flex: 1 },
  {
    field: 'pof',
    headerName: 'POF',
    flex: 1,
    renderCell: (parameters: GridCellParams<RowData>) => renderCalculationCell(parameters, 'pof'),
  },
  {
    field: 'cof',
    headerName: 'COF',
    flex: 1,
    renderCell: (parameters: GridCellParams<RowData>) => renderCalculationCell(parameters, 'cof'),
  },
  {
    field: 'transitionPeriod',
    headerName: 'Transition Period',
    flex: 1,
    renderCell: (parameters: GridCellParams<RowData>) =>
      renderCalculationCell(parameters, 'transitionPeriod'),
  },
];

export const EsSheetSummaryTable = ({
  structure,
  handleUnpublish,
  loadingUnpublish,
  riskAssessmentDone,
}: EsSheetSummaryTableProps) => {
  const esSheetStatus = structure?.esSheetStatus;
  const isPublished = esSheetStatus === EsSheetStatus.Published;
  const [isRowExpand, setIsRowExpand] = useState<number | boolean>(false);
  const [sheetData, setSheetData] = useState<
    GetRiskAssessmentSummaryDataQuery['getRiskAssessmentSummaryData']
  >([]);
  const [filteredData, setFilteredData] = useState<typeof sheetData>([]);
  const [selectedLevel1Component, setSelectedLevel1Component] = useState<string>('');
  const [selectedFilters, setSelectedFilters] = useState<{ id: string; value: string }[]>([]);
  const setSelectedRowFilters = useSetAtom(selectedRiskAssessmentFilters);
  const [alertDialog, setAlertDialog] = useState(false);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const setView = useSetAtom(riskView);
  const router = useRouter();
  const isMTEngineer = useFeatureFlag('mt-application-engineer');

  const { filters, loading: filtersLoading } = useFiltersForRisk({
    source: 'summary',
    selectedFilters,
  });

  const { data, loading } = useGetRiskAssessmentSummaryDataQuery({
    variables: { input: { structureId: structure?.id ?? '', selectedFilters } },
  });

  const { data: summaryTableData, loading: tableDataLoading } =
    useGetRiskAssessmentSummaryTableDataQuery({
      variables: {
        input: {
          structureId: structure?.id ?? '',
          selectedLevel1Component,
          selectedFilters,
        },
      },
      skip: !selectedLevel1Component,
    });

  const esSheetSubTableRowsData = useMemo(
    () => summaryTableData?.getRiskAssessmentSummaryTableData || [],
    [summaryTableData?.getRiskAssessmentSummaryTableData]
  );

  useEffect(() => {
    const esSheetSummaryData = data?.getRiskAssessmentSummaryData || [];
    setSheetData(esSheetSummaryData);
    setFilteredData(esSheetSummaryData);
  }, [data?.getRiskAssessmentSummaryData]);

  const keysToUse = isPublished ? riskCategoryKeys : damageClassKeys;

  const esSheetTableColumns = useMemo(
    () => [
      { name: 'Level 1 Component', key: 'level1Component' },
      ...keysToUse.map((key) => ({ name: key, key })),
      { name: 'Calculations', key: 'calculations' },
    ],
    [keysToUse]
  );

  const esSheetTableRows = filteredData
    ?.map((item) => {
      const damageClassCount = Object.fromEntries(
        (item?.damageClassCount || []).map((dc) => [dc?.key, dc?.count])
      );
      return {
        structureType: item?.level1Component,
        ...Object.fromEntries(damageClassKeys.map((key) => [key, damageClassCount[key] || 0])),
        calculations: `${item?.completedCalculationsCount}/${item?.totalCalculationsCount}`,
      };
    })
    .slice(0, isPublished ? 4 : undefined);

  const handleSearchStructureType = useCallback(
    (searchValue: string) => {
      const filtered = matchSorter(sheetData || [], searchValue, {
        keys: ['level1Component'],
      });
      setFilteredData(filtered);
    },
    [sheetData]
  );

  const esSheetSubTableRows = useMemo(() => {
    return esSheetSubTableRowsData.map((row, index) => {
      const {
        risk_level_1_component,
        risk_level_2_component,
        risk_structure,
        calculations,
        risk_damage_class,
      } = row;
      const { pof, cof, transitionPeriod } = calculations;
      return {
        id: index.toString(),
        risk_level_1_component,
        risk_level_2_component,
        risk_structure,
        risk_damage_class,
        pof,
        cof,
        transitionPeriod,
        calculations: JSON.stringify(calculations),
      };
    });
  }, [esSheetSubTableRowsData]);

  useEffect(() => {
    if (selectedFilters.length === 0) {
      setIsRowExpand(false);
    }
  }, [selectedFilters.length]);

  const handleExpandCell = useCallback(
    (level1Component: string, index: number) => {
      setIsRowExpand((previous) => (previous === index ? false : index));
      setSelectedLevel1Component(level1Component);
      setSelectedRowFilters([]);
      setSelectedRows([]);
    },
    [setSelectedRowFilters]
  );

  const handleFiltersChange = useCallback(
    (newFilters: Record<string, string[]>) => {
      const appliedFilters = Object.entries(newFilters).map(([key, values]) =>
        values.map((value) => ({ id: key, value }))
      );
      setSelectedFilters(appliedFilters.flat());
    },
    [setSelectedFilters]
  );

  const handleSelectionChange = (rows: string[]) => {
    const rowsData = esSheetSubTableRows.filter((row) => rows.includes(row.id.toString()));
    const isLevel2ComponentSame = rowsData.every(
      (row) => row.risk_level_2_component === rowsData[0].risk_level_2_component
    );
    if (isLevel2ComponentSame) setSelectedRows(rows);
    else setAlertDialog(true);
  };

  useEffect(() => {
    const rowsData = esSheetSubTableRows.filter((row) => selectedRows.includes(row.id.toString()));

    const uniqueComponents = new Set();
    const rowFilters = rowsData.flatMap(
      ({ risk_level_1_component, risk_level_2_component, risk_structure }) => {
        const rowData: Record<string, string> = {
          risk_level_1_component,
          risk_level_2_component,
          risk_structure,
        };

        const currentRowFilter = Object.keys(rowData).map((key) => ({
          id: key,
          value: rowData[key],
        }));

        return currentRowFilter.filter((filter) => {
          if (!filter.id || !filter.value) return false;
          const isMultiSelect =
            filters.find((f) => f.key === filter.id)?.type === FilterType.MULTI_SELECT;
          if (isMultiSelect) return true;
          if (uniqueComponents.has(filter.id)) return false;
          uniqueComponents.add(filter.id);
          return true;
        });
      }
    );

    setSelectedRowFilters(rowFilters);
  }, [esSheetSubTableRows, filters, selectedRows, setSelectedRowFilters]);

  const navigateToCalculations = useCallback(() => {
    const currentQuery = { ...router.query, view: 'calculations' };
    router.push({
      pathname: router.pathname,
      query: currentQuery,
    });
    setView('calculations');
  }, [router, setView]);

  const cursorStyle = isPublished ? { '&:hover': { cursor: 'auto !important' } } : {};

  const handleReviewRowClick = useCallback(
    (structureType: string) => {
      if (!isPublished) return;
      setSelectedRowFilters([{ id: 'risk_level_1_component', value: structureType }]);
      navigateToCalculations();
    },
    [isPublished, navigateToCalculations, setSelectedRowFilters]
  );

  const handleSingleRowClick = useCallback(
    (row: RowData) => {
      setSelectedRowFilters([
        {
          id: 'risk_level_1_component',
          value: row.risk_level_1_component.toString() ?? '',
        },
        {
          id: 'risk_level_2_component',
          value: row?.risk_level_2_component.toString() ?? '',
        },
        { id: 'risk_structure', value: row?.risk_structure.toString() ?? '' },
        { id: 'risk_damage_class', value: row?.risk_damage_class.toString() ?? '' },
      ]);
      navigateToCalculations();
    },
    [setSelectedRowFilters, navigateToCalculations]
  );

  const handlePublishedClick = () => {
    if (isMTEngineer) {
      handleUnpublish();
    } else {
      navigateToCalculations();
    }
  };

  const tooltipTitle = useMemo(() => {
    if (!riskAssessmentDone) {
      return 'Complete asset setup';
    }
    return '';
  }, [riskAssessmentDone]);

  return (
    <Box
      sx={{
        mb: 8,
        position: 'relative',
        marginTop: isPublished ? '2.4rem' : 0,
      }}
    >
      {isPublished && (
        <DisabledOverlay>
          <Stack alignItems="center">
            <AbyssIcon
              iconName="notification"
              style={{
                transform: 'rotate(27.964deg)',
                marginBottom: '2.4rem',
                height: '4rem',
              }}
            />
            <Typography
              textAlign="center"
              maxWidth="64rem"
              fontSize="1.6rem"
              marginBottom="1.6rem"
              color={abyssColors.primary[400]}
            >
              {isMTEngineer
                ? 'The ES sheet is now published and is view-only. Please unpublish it to enable calculations for the MT inspector.'
                : 'The ES sheet is published and view-only. Please ask the MT engineer to unpublish it to enable calculations.'}
            </Typography>
            <ActionButton contained onClick={handlePublishedClick} disabled={loadingUnpublish}>
              <AbyssIcon
                iconName="file-upload-line"
                style={{ width: '1.6rem', height: '1.6rem' }}
              />
              <span>{isMTEngineer ? 'Unpublish ES sheet' : 'Risk Matrix'}</span>
            </ActionButton>
          </Stack>
        </DisabledOverlay>
      )}
      <Stack marginX="1.6rem">
        <FiltersPanel
          filters={filters}
          disabled={filtersLoading}
          loading={loading || filtersLoading}
          onApply={handleFiltersChange}
          handleSearch={handleSearchStructureType}
          rightComponent={<CalculationsMenu />}
          filterInputWidth="25rem"
        />
        <TableContainer sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ display: 'flex', width: '100%' }}>
                {esSheetTableColumns.map(({ key, name }) => (
                  <StyledTableCell
                    key={key}
                    sx={{
                      py: 2,
                      fontSize: '12px',
                      fontWeight: 400,
                      color: abyssColors.primary[400],
                      ...(key === 'level1Component' ? { width: '18%' } : {}),
                    }}
                  >
                    {name}
                  </StyledTableCell>
                ))}
                <StyledTableCell sx={{ width: '5.8%', py: 2 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {esSheetTableRows?.map((row, index) => {
                const rowOnClick = isPublished
                  ? () => {}
                  : () => handleExpandCell(row.structureType ?? '', index);
                return (
                  <StyledTableRow
                    key={`${row.structureType}-${index}`}
                    sx={{ display: 'flex', height: 'auto', ...cursorStyle }}
                  >
                    <Accordion
                      disableGutters
                      elevation={0}
                      expanded={isRowExpand === index && riskAssessmentDone}
                      sx={{ ...accordionStyles.root, mb: 1, width: '100%' }}
                    >
                      <StyledAccordionSummary
                        onClick={rowOnClick}
                        sx={{
                          px: 0,
                          backgroundColor: abyssColors.primary[50],
                          ...cursorStyle,
                        }}
                      >
                        <StyledTableCell
                          sx={{
                            width: '18%',
                            fontSize: 16,
                            fontWeight: 700,
                            alignItems: 'center',
                            color: abyssColors.primary[500],
                          }}
                        >
                          <Tooltip title={row.structureType} placement="top" arrow>
                            <Box
                              sx={{
                                width: 185,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {row.structureType ?? ''}
                            </Box>
                          </Tooltip>
                        </StyledTableCell>
                        {damageClassKeys.map((key) => (
                          <StyledTableCell key={key}>
                            <CellColorBox color={getCriticalityLevelColor(key)} />
                            {row[key as keyof typeof row]}
                          </StyledTableCell>
                        ))}
                        <StyledTableCell style={{ color: abyssColors.primary[500] }}>
                          {row.calculations}
                        </StyledTableCell>
                        <StyledTableCell sx={{ width: '5.8%' }}>
                          <Tooltip
                            title={tooltipTitle}
                            placement="bottom"
                            arrow
                            slotProps={{
                              popper: {
                                sx: {
                                  '& .MuiTooltip-tooltip': {
                                    backgroundColor: '#000000B8',
                                    color: abyssColors.primary[50],
                                    opacity: 1,
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontWeight: 500,
                                  },
                                  '& .MuiTooltip-arrow': {
                                    color: '#000000B8',
                                  },
                                },
                              },
                            }}
                          >
                            <span>
                              <IconButton
                                disabled={!riskAssessmentDone}
                                onClick={() => handleReviewRowClick(row.structureType ?? '')}
                              >
                                {isRowExpand === index && riskAssessmentDone ? (
                                  <UpArrow />
                                ) : (
                                  <DownArrow />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </StyledTableCell>
                      </StyledAccordionSummary>
                      <AccordionDetails sx={{ p: 2 }}>
                        {tableDataLoading && <PageLoader />}
                        {isRowExpand === index && !tableDataLoading && (
                          <DataTable
                            rows={esSheetSubTableRows}
                            selectedRows={selectedRows}
                            columns={esSheetSubTableColumns}
                            handleSelectionChange={handleSelectionChange}
                            onRowClick={handleSingleRowClick}
                            sxProps={{
                              '& .MuiDataGrid-row:hover': {
                                backgroundColor: abyssColors.primary[50],
                              },
                              '& .MuiDataGrid-row.Mui-selected': {
                                backgroundColor: abyssColors.primary[50],
                                outline: 'none',
                              },
                              '& .MuiDataGrid-row.Mui-selected:hover': {
                                backgroundColor: abyssColors.primary[50],
                              },
                              '& .MuiDataGrid-cell:focus-within': {
                                outline: 'none',
                              },
                            }}
                          />
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </StyledTableRow>
                );
              })}
              {esSheetTableRows?.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    sx={{
                      textAlign: 'center',
                      alignItems: 'center',
                      color: abyssColors.primary[400],
                    }}
                  >
                    <InfoOutlined sx={{ color: abyssColors.primary[400], fontSize: '6rem' }} />
                    <Typography
                      variant="h6"
                      fontWeight={500}
                      sx={{
                        mt: 1,
                        color: abyssColors.primary[400],
                        whiteSpace: 'nowrap',
                      }}
                    >
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {alertDialog && (
          <GenericDialog
            open={alertDialog}
            onClose={() => setAlertDialog(false)}
            title="Different Formulas"
            content={
              <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
                Please select structure descriptions that match the corresponding calculation
                formulas.
              </Typography>
            }
            actions={
              <Button
                variant="contained"
                onClick={() => setAlertDialog(false)}
                sx={containedButtonStyle}
              >
                GOT IT
              </Button>
            }
            actionsStyles={{ mr: 3, pb: 3 }}
          />
        )}
      </Stack>
    </Box>
  );
};
